/**
 * Real DWI registration + lesion-segmentation sequence, factored out so both
 * the main pipeline runner (`wasm-engine/engine.ts`) and the standalone
 * Tools-menu entries ("Register to MNI Space" / "Segment Stroke Core") call
 * the exact same, already-validated logic instead of two drifting copies.
 *
 * Ported verbatim (not re-derived) from `openads-wasm/app/src/main.ts`'s
 * `runRegistrationStage()`/`runLesionSeg()` DWI branch, which is itself
 * reconfirmed against the real OpenADS `pipe_registration.py` (registration
 * sequencing: NCC=0.9984 vs native ANTs) — see that file's inline comments
 * for the full citation trail. Do not "clean up" the sequencing or the ADC
 * x1000 scaling below; both are load-bearing fixes for real bugs found
 * during that validation, not arbitrary choices.
 *
 * CORRECTION (superseding every earlier Dice figure cited for this file,
 * 0.8439/0.8451): those numbers were measured with segmentation fed the
 * SyN-registered (affsyn) volumes, which is NOT what the real pipeline does
 * — see `runDwiSegmentation`'s own doc comment below for the citation. The
 * registration sequence above (Affine then SyN) is still correct and still
 * needed (SyN registration genuinely runs, its output is still a real,
 * useful product), the bug was only in which of its outputs gets handed to
 * segmentation.
 */
import { runAntsApplyTransform, runAntsRegistration } from "./ants-registration-client";
import type { NiftiVolume } from "./nifti";
import { ab, runWorker } from "./worker-client";
import type { Dims } from "./volume";

import { appPath } from "./assets";
export interface DwiRegistrationResult {
  affineDwi: NiftiVolume;
  affineAdc: NiftiVolume;
  affineMask: NiftiVolume;
  /** 0/1, same grid as affineDwi/affineAdc — the real inference/scoring space (see runDwiSegmentation). */
  affineMaskBinary: Uint8Array;
  /** Present only when `rawB0` was passed in — real OpenADS registers B0 the
   *  same way as DWI/ADC (`registration_align.py`'s `images = {'dwi','adc','mask','b0',...}`
   *  dict, one shared affine/SyN apply-transform call set) purely for output-
   *  file completeness; nothing downstream (segmentation, reporting) consumes it. */
  affineB0?: NiftiVolume;
  registeredB0?: NiftiVolume;
  /** SyN-stage outputs: absent when the run skipped SyN (`skipSyn`), in
   *  which case every consumer falls back to the affine-space volumes. */
  registeredDwi?: NiftiVolume;
  registeredAdc?: NiftiVolume;
  /** 0/1, same grid as registeredDwi/registeredAdc. Kept for the optional affsyn export (registration QC), NOT used for segmentation — see runDwiSegmentation's own doc comment. */
  registeredMask?: Uint8Array;
  /** `/out_affine_0GenericAffine.mat` bytes, kept for export/debugging. */
  dwiAffineTransform: ArrayBuffer;
  /** `/out_syn_1Warp.nii.gz` bytes (already-gzipped NIfTI) — the real forward SyN warp field. */
  synWarpField?: ArrayBuffer;
  /** `/out_syn_0GenericAffine.mat` bytes — SyN's own affine component. */
  synAffineTransform?: ArrayBuffer;
  /** `/out_syn_1InverseWarp.nii.gz` bytes — real ANTs SyN produces this as a
   *  direct, intrinsic byproduct of the symmetric-normalization optimization
   *  (not extra computation); the worker already captures it, just wasn't
   *  surfaced to callers before. Matches native's `{id}_invwarp_space-
   *  MNI1522MNI152.nii.gz` (`transform_io.py`'s `save_transforms('affsyn')`,
   *  `invtransforms` -> the file containing `InverseWarp.nii.gz`). */
  synInverseWarpField?: ArrayBuffer;
  /** True when SyN was not run; segmentation and reporting never needed it. */
  synSkipped: boolean;
}

function toVol(r: { dims: Dims; pixdim: Dims; affine: Float32Array; registered: Float32Array }): NiftiVolume {
  return { dims: r.dims, pixdim: r.pixdim, affine: r.affine, voxels: r.registered, datatype: 16 };
}

/** Real OpenADS `utils/masking.py`: `np.where(mask, img, 0)`. */
function applyMask(vol: NiftiVolume, mask: Uint8Array): NiftiVolume {
  const voxels = new Float32Array(vol.voxels.length);
  for (let i = 0; i < voxels.length; i++) voxels[i] = mask[i] ? vol.voxels[i] : 0;
  return { ...vol, voxels };
}

/**
 * Real sequence (reconfirmed against `pipe_registration.py` in full):
 *   1. Affine: moving = SynthStrip brain MASK (not DWI, not ADC).
 *   2. antsApplyTransforms(affine transform) -> raw DWI, raw ADC (each its
 *      own resample, fixed = the same DWI-mask template Affine used).
 *   3. SyN: moving = the AFFINE-REGISTERED ADC from step 2 (not DWI).
 *      `warpedmovout` IS the final registered ADC — no separate apply step.
 *   4. antsApplyTransforms(SyN transform) -> the AFFINE-REGISTERED DWI from
 *      step 2 (fixed = the ADC template SyN used) -> final registered DWI.
 *   5. antsApplyTransforms(SyN transform, nearest-neighbor) -> the
 *      AFFINE-REGISTERED mask -> final registered mask.
 *
 * `rawDwi`/`rawAdc` must be in the subject's own native space (not yet
 * skull-stripped — this function strips them itself, matching real
 * OpenADS calling skull-stripping before registration, not the other way
 * round), and `nativeBrainMask` must be SynthStrip's output on that same
 * native grid.
 */
export async function runDwiRegistration(
  rawDwi: NiftiVolume,
  rawAdc: NiftiVolume,
  nativeBrainMask: Uint8Array,
  onProgress: (stage: string) => void,
  rawB0?: NiftiVolume,
  opts: { skipSyn?: boolean } = {},
): Promise<DwiRegistrationResult> {
  const maskVol: NiftiVolume = {
    dims: rawDwi.dims,
    pixdim: rawDwi.pixdim,
    affine: rawDwi.affine,
    voxels: Float32Array.from(nativeBrainMask),
    datatype: 16,
  };
  const strippedDwi = applyMask(rawDwi, nativeBrainMask);
  const strippedAdc = applyMask(rawAdc, nativeBrainMask);

  onProgress("affine: registering brain mask to JHU_MNI_SS_DWI_mask_to_MNI");
  const affineResult = await runAntsRegistration(
    { step: "affine", voxels: maskVol.voxels, dims: maskVol.dims, pixdim: maskVol.pixdim, affine: maskVol.affine },
    (s) => onProgress(`affine: ${s}`),
  );
  const affAffineMat = affineResult.transformFiles["/out_affine_0GenericAffine.mat"];
  if (!affAffineMat) throw new Error("Registration: affine transform file missing from worker output");
  const affineMaskVol = toVol(affineResult);
  const affineMaskBinary = new Uint8Array(affineMaskVol.voxels.length);
  for (let i = 0; i < affineMaskVol.voxels.length; i++) affineMaskBinary[i] = affineMaskVol.voxels[i] > 0.5 ? 1 : 0;

  // Sequential, not parallel: the worker has no request-correlation id, so
  // two concurrent calls race and cross-talk (hit for real during the
  // original validation — see ants-registration.worker.ts's header).
  onProgress("applying affine transform to DWI");
  const affineDwiResult = await runAntsApplyTransform(
    {
      voxels: strippedDwi.voxels,
      dims: strippedDwi.dims,
      pixdim: strippedDwi.pixdim,
      affine: strippedDwi.affine,
      transforms: [{ path: "/aff.mat", bytes: affAffineMat }],
      fixedTemplate: "affine",
    },
    (s) => onProgress(`apply-affine-dwi: ${s}`),
  );
  onProgress("applying affine transform to ADC");
  const affineAdcResult = await runAntsApplyTransform(
    {
      voxels: strippedAdc.voxels,
      dims: strippedAdc.dims,
      pixdim: strippedAdc.pixdim,
      affine: strippedAdc.affine,
      transforms: [{ path: "/aff.mat", bytes: affAffineMat }],
      fixedTemplate: "affine",
    },
    (s) => onProgress(`apply-affine-adc: ${s}`),
  );
  const affineDwiVol = toVol(affineDwiResult);
  const affineAdcVol = toVol(affineAdcResult);

  let affineB0Vol: NiftiVolume | undefined;
  if (rawB0) {
    const strippedB0 = applyMask(rawB0, nativeBrainMask);
    onProgress("applying affine transform to B0");
    const affineB0Result = await runAntsApplyTransform(
      {
        voxels: strippedB0.voxels,
        dims: strippedB0.dims,
        pixdim: strippedB0.pixdim,
        affine: strippedB0.affine,
        transforms: [{ path: "/aff.mat", bytes: affAffineMat }],
        fixedTemplate: "affine",
      },
      (s) => onProgress(`apply-affine-b0: ${s}`),
    );
    affineB0Vol = toVol(affineB0Result);
  }

  // SyN is ~7-8 min single-threaded and nothing downstream reads it:
  // segmentation, reporting and PWI all run in MNI-affine space. Skipping it
  // drops only the affsyn QC outputs.
  if (opts.skipSyn) {
    onProgress("syn: skipped (affine-only run) — affsyn outputs fall back to aff");
    return {
      affineDwi: affineDwiVol,
      affineAdc: affineAdcVol,
      affineMask: affineMaskVol,
      affineMaskBinary,
      affineB0: affineB0Vol,
      dwiAffineTransform: affAffineMat,
      synSkipped: true,
    };
  }

  // ADC's native magnitude (~1e-3) amplifies WASM-vs-native float32
  // precision divergence during SyN's Mattes MI optimization; rescaling to
  // roughly unit magnitude before SyN and back after restores NCC=0.999
  // fidelity (vs 0.955 unscaled) — both normalization steps downstream are
  // scale-invariant, so this changes nothing except SyN's own conditioning.
  const ADC_SYN_SCALE = 1000;
  const scaledAdcVoxels = new Float32Array(affineAdcVol.voxels.length);
  for (let i = 0; i < scaledAdcVoxels.length; i++) scaledAdcVoxels[i] = affineAdcVol.voxels[i] * ADC_SYN_SCALE;

  onProgress("syn: registering affine-ADC to JHU_MNI_SS_ADC_ss_to_MNI (slow stage)");
  const synResult = await runAntsRegistration(
    { step: "syn", voxels: scaledAdcVoxels, dims: affineAdcVol.dims, pixdim: affineAdcVol.pixdim, affine: affineAdcVol.affine },
    (s) => onProgress(`syn: ${s}`),
  );
  const registeredAdcScaled = toVol(synResult);
  const unscaledAdcVoxels = new Float32Array(registeredAdcScaled.voxels.length);
  for (let i = 0; i < unscaledAdcVoxels.length; i++) unscaledAdcVoxels[i] = registeredAdcScaled.voxels[i] / ADC_SYN_SCALE;
  const registeredAdc: NiftiVolume = { ...registeredAdcScaled, voxels: unscaledAdcVoxels };

  const synAffineMat = synResult.transformFiles["/out_syn_0GenericAffine.mat"];
  const synWarpGz = synResult.transformFiles["/out_syn_1Warp.nii.gz"];
  if (!synAffineMat || !synWarpGz) throw new Error("Registration: SyN transform files missing from worker output");
  // Optional, output-completeness-only: the worker stages this file only if
  // antsRegistration actually wrote it (try/catch "optional file not
  // produced; skip" in ants-registration.worker.ts) — don't hard-fail the
  // whole (already-verified, Dice-critical) registration stage for want of
  // this QC byproduct.
  const synInverseWarpGz = synResult.transformFiles["/out_syn_1InverseWarp.nii.gz"];

  onProgress("applying syn transform to affine-DWI");
  const dwiApplyResult = await runAntsApplyTransform(
    {
      voxels: affineDwiVol.voxels,
      dims: affineDwiVol.dims,
      pixdim: affineDwiVol.pixdim,
      affine: affineDwiVol.affine,
      transforms: [
        { path: "/syn_warp.nii.gz", bytes: synWarpGz },
        { path: "/syn_aff.mat", bytes: synAffineMat },
      ],
      fixedTemplate: "syn",
    },
    (s) => onProgress(`apply-syn-dwi: ${s}`),
  );
  const registeredDwi = toVol(dwiApplyResult);

  let registeredB0Vol: NiftiVolume | undefined;
  if (affineB0Vol) {
    onProgress("applying syn transform to affine-B0");
    const b0ApplyResult = await runAntsApplyTransform(
      {
        voxels: affineB0Vol.voxels,
        dims: affineB0Vol.dims,
        pixdim: affineB0Vol.pixdim,
        affine: affineB0Vol.affine,
        transforms: [
          { path: "/syn_warp.nii.gz", bytes: synWarpGz },
          { path: "/syn_aff.mat", bytes: synAffineMat },
        ],
        fixedTemplate: "syn",
      },
      (s) => onProgress(`apply-syn-b0: ${s}`),
    );
    registeredB0Vol = toVol(b0ApplyResult);
  }

  onProgress("applying syn transform to affine-mask (nearest-neighbor)");
  const maskApplyResult = await runAntsApplyTransform(
    {
      voxels: affineMaskVol.voxels,
      dims: affineMaskVol.dims,
      pixdim: affineMaskVol.pixdim,
      affine: affineMaskVol.affine,
      transforms: [
        { path: "/syn_warp.nii.gz", bytes: synWarpGz },
        { path: "/syn_aff.mat", bytes: synAffineMat },
      ],
      fixedTemplate: "syn",
      interpolator: "nearestNeighbor",
    },
    (s) => onProgress(`apply-syn-mask: ${s}`),
  );
  const registeredMaskVol = toVol(maskApplyResult);
  const registeredMask = new Uint8Array(registeredMaskVol.voxels.length);
  for (let i = 0; i < registeredMaskVol.voxels.length; i++) registeredMask[i] = registeredMaskVol.voxels[i] > 0.5 ? 1 : 0;

  return {
    affineDwi: affineDwiVol,
    affineAdc: affineAdcVol,
    affineMask: affineMaskVol,
    affineMaskBinary,
    affineB0: affineB0Vol,
    registeredB0: registeredB0Vol,
    registeredDwi,
    registeredAdc,
    registeredMask,
    dwiAffineTransform: affAffineMat,
    /** `/out_syn_1Warp.nii.gz` bytes — the real forward SyN warp field, already produced by the SyN call above, just not previously surfaced to the caller. */
    synWarpField: synWarpGz,
    synAffineTransform: synAffineMat,
    synInverseWarpField: synInverseWarpGz,
    synSkipped: false,
  };
}

/**
 * Warp a native-space ground-truth label volume into the aff/affsyn MNI
 * spaces this pipeline already computed transforms for — real native
 * pipeline writes these as optional QC outputs whenever a ground-truth
 * stroke mask was actually supplied (not part of a real clinical run
 * without a reference label). Reuses the exact same `runAntsApplyTransform`
 * nearest-neighbor pattern already proven correct for the affine-space mask
 * warp above and for this project's own DWI Dice verification script
 * (`scripts/verify-full-dwi-pipeline.mjs`'s `applyTransform(gtNative,
 * fixedAffine, [...], "nearestNeighbor")`) — not a new algorithm.
 */
export async function warpGroundTruthToMni(
  gtNative: NiftiVolume,
  reg: DwiRegistrationResult,
  onProgress: (stage: string) => void,
): Promise<{ aff: NiftiVolume; affsyn: NiftiVolume | null }> {
  onProgress("warping ground truth: affine");
  const affResult = await runAntsApplyTransform(
    {
      voxels: gtNative.voxels,
      dims: gtNative.dims,
      pixdim: gtNative.pixdim,
      affine: gtNative.affine,
      transforms: [{ path: "/aff.mat", bytes: reg.dwiAffineTransform }],
      fixedTemplate: "affine",
      interpolator: "nearestNeighbor",
    },
    (s) => onProgress(`warp-gt-affine: ${s}`),
  );
  const aff = toVol(affResult);

  let affsyn: NiftiVolume | null = null;
  if (reg.synWarpField && reg.synAffineTransform) {
    // Same chain as the affine-space mask's own SyN warp above (affine
    // transform's output is the SyN stage's moving-space input).
    onProgress("warping ground truth: syn");
    const affsynResult = await runAntsApplyTransform(
      {
        voxels: aff.voxels,
        dims: aff.dims,
        pixdim: aff.pixdim,
        affine: aff.affine,
        transforms: [
          { path: "/syn_warp.nii.gz", bytes: reg.synWarpField },
          { path: "/syn_aff.mat", bytes: reg.synAffineTransform },
        ],
        fixedTemplate: "syn",
        interpolator: "nearestNeighbor",
      },
      (s) => onProgress(`warp-gt-syn: ${s}`),
    );
    affsyn = toVol(affsynResult);
  }
  return { aff, affsyn };
}

export interface DwiSegmentationResult {
  mask: Uint8Array;
  dims: Dims;
  lesionVoxels: number;
  totalMs: number;
}

/** Real DAGMNet DWI (ch3) ONNX model.
 *
 *  **Input space: MNI152-AFFINE-ONLY (`_aff`), NOT Affine+SyN (`_affsyn`).**
 *  Confirmed directly against the real CLI entry point
 *  (`scripts/run_ads_dwi.py`'s `SegmentationInputs` construction): the real
 *  pipeline's `dwi_mni`/`adc_mni`/`mask_mni` are `*_space-MNI152_aff.nii.gz`
 *  files, and the ground truth it scores against is likewise
 *  `*_stroke_space-MNI152_aff.nii.gz`. SyN (`affsyn`) only re-enters the real
 *  pipeline afterward, as an optional step that warps the ALREADY-COMPUTED
 *  prediction into affsyn space for a secondary output file
 *  (`segmentation_dwi_dagmnet.py`'s "Step 9") — it is never the inference
 *  input. Earlier versions of this port fed the SyN-registered volumes here
 *  instead, which silently diverged from the real pipeline; every Dice
 *  number measured before this fix was computed in the wrong space against
 *  mismatched the wrong ground truth file and should not be cited.
 *
 *  Pass `reg.affineDwi`/`reg.affineAdc`/`reg.affineMaskBinary` from
 *  `runDwiRegistration`'s result, not the `registered*` (SyN) fields.
 *
 *  `mask`, when given, is the affine-space brain mask used to scope the
 *  model's own percentile-clip/z-score normalization (see
 *  ads/modalities/dwi/segmentation.py's `get_dwi_normalized`). */
export async function runDwiSegmentation(
  dwi: NiftiVolume,
  adc: NiftiVolume,
  mask: Uint8Array | null,
  onProgress: (stage: string) => void,
): Promise<DwiSegmentationResult> {
  const worker = new Worker(appPath("workers/dagmnet-dwi.worker.js"), { type: "module" });
  try {
    const result = await runWorker<
      { dwi: ArrayBuffer; adc: ArrayBuffer; dims: Dims; affine: ArrayBuffer; mask?: ArrayBuffer },
      { mask: ArrayBuffer; dims: Dims; lesionVoxels: number; totalMs: number }
    >(
      worker,
      {
        dwi: ab(dwi.voxels),
        adc: ab(adc.voxels),
        dims: dwi.dims,
        affine: ab(dwi.affine),
        mask: mask ? ab(mask) : undefined,
      },
      onProgress,
    );
    return { mask: new Uint8Array(result.mask), dims: result.dims, lesionVoxels: result.lesionVoxels, totalMs: result.totalMs };
  } finally {
    worker.terminate();
  }
}
